import { Limit, Offer_Engagement_Type, OfferFragment } from '@occc/graphql';
import { getEarningMechanismAward } from '@occc-app/offerHelpers/utils/getEarningMechanismAward';

import {
  OfferSchema,
  OfferSchemaAward,
  OfferSchemaConstraint,
  OfferSchemaEarningMechanism,
  OfferSchemaLimit,
  OfferSchemaRequiredValues,
  PartialOfferFragment,
  PartialOfferSchema,
} from './types';
import { schemaList } from './schemas';
import { isOfferFragment } from './utils';
import { offerUtils } from './utils/offerUtils';

export class OfferValidationService {
  private validateAward = (award: OfferSchemaAward, supportedAwards: OfferSchemaAward[]) => {
    return supportedAwards.includes(award);
  };

  private validateEarningMechanism = (
    earningMechanism: OfferSchemaEarningMechanism,
    supportedEarningMechanisms: OfferSchemaEarningMechanism[],
  ) => {
    return supportedEarningMechanisms.includes(earningMechanism);
  };

  private validateLimits = (limits: Limit[], supportedLimits: OfferSchemaLimit[]) => {
    return limits.every((limit) => {
      return supportedLimits.includes(limit.__typename);
    });
  };

  private validateConstraints = (
    constraints: OfferFragment['constraints'],
    supportedConstraints: OfferSchemaConstraint[],
  ) => {
    return constraints.every((constraint) => {
      return supportedConstraints.includes(constraint.__typename);
    });
  };

  private checkRequiredValues = (
    offer: OfferFragment,
    requiredValues: OfferSchemaRequiredValues,
  ) => {
    if (!requiredValues.constraints) {
      return true;
    }
    return requiredValues.constraints.every((constraint) => {
      return offer.constraints.map((item) => item.__typename).includes(constraint);
    });
  };

  validateByPartialOfferData = (
    offer: PartialOfferFragment | OfferFragment,
    schema: OfferSchema,
  ): boolean => {
    if (offer.engagementType !== schema.engagementType) {
      return false;
    }

    const earningMechanism = offer.earningMechanism ? offer.earningMechanism.__typename : null;

    if (!this.validateEarningMechanism(earningMechanism, schema.earningMechanism)) {
      return false;
    }

    const award = offer.earningMechanism ? getEarningMechanismAward(offer.earningMechanism) : null;
    if (award && !this.validateAward(award.__typename, schema.awards)) {
      return false;
    }

    return schema.validate ? schema.validate(offer) : true;
  };

  validateByOfferData = (offer: OfferFragment, schema: OfferSchema): boolean => {
    const isPartiallyValid = this.validateByPartialOfferData(offer, schema);
    if (!isPartiallyValid) {
      return false;
    }

    const isLimitsValid = this.validateLimits(offer.limits, schema.limits);
    if (!isLimitsValid) {
      return false;
    }

    const isConstraintsValid = this.validateConstraints(offer.constraints, schema.constraints);
    if (!isConstraintsValid) {
      return false;
    }

    return schema.requiredValues ? this.checkRequiredValues(offer, schema.requiredValues) : true;
  };

  getType<T extends PartialOfferFragment | OfferFragment>(
    offer: T,
    validate: (offer: T, schema: OfferSchema) => boolean,
  ): PartialOfferSchema | null {
    const validSchemas: PartialOfferSchema[] = [];

    // TODO: It would be great to review the logic inside this if statement
    // I'm not sure why we don't use the same check for awards
    // Ideally, it should be the same code for offers and awards
    if (
      !offer.earningMechanism &&
      offer.engagementType !== Offer_Engagement_Type.Vouchered &&
      offer.engagementType !== Offer_Engagement_Type.SweepstakesEntry &&
      offer.engagementType !== Offer_Engagement_Type.Billboard
    ) {
      const type = offer.engagementType
        ? offerUtils.getOfferTypeByEngagementType(offer.engagementType)
        : null;
      const typeFromEngagementType = type ? { type } : null;

      if (isOfferFragment(offer)) {
        if (offer.limits.length === 0 && offer.constraints.length === 0) {
          return typeFromEngagementType;
        } else {
          return null;
        }
      } else {
        return typeFromEngagementType;
      }
    }

    for (const schema of schemaList) {
      const isValidSchema = validate(offer, schema);

      if (isValidSchema) {
        validSchemas.push({
          type: schema.type,
          subtype: schema.subtype,
        });
      }
    }
    // 'Multiple schemas are valid for this offer!'
    if (validSchemas.length > 1) {
      return null;
    }

    if (validSchemas.length === 1) {
      return validSchemas[0];
    }

    return null;
  }

  getTypeFromOfferData(offer: OfferFragment) {
    return this.getType(offer, this.validateByOfferData);
  }

  getTypeFromPartialOfferData(offer: PartialOfferFragment) {
    return this.getType(offer, this.validateByPartialOfferData);
  }

  getSchema(offer: OfferFragment | PartialOfferFragment): OfferSchema | null {
    const isOffer = isOfferFragment(offer);

    const result = isOffer
      ? this.getTypeFromOfferData(offer)
      : this.getTypeFromPartialOfferData(offer);

    if (!result) return null;

    if (result.subtype !== undefined) {
      const schema = schemaList.find(
        (s) => s.type === result!.type && s.subtype === result!.subtype,
      );
      return schema ?? null;
    }

    for (const schema of schemaList) {
      if (isOffer) {
        if (this.validateByOfferData(offer, schema)) return schema;
      } else {
        if (this.validateByPartialOfferData(offer, schema)) return schema;
      }
    }
    return null;
  }
}

export const offerValidationService = new OfferValidationService();
